/**
 * Venue scope store + hooks (#689). A tiny external store (no provider needed): the build config comes from
 * import.meta.env, the per-env override from localStorage (hip4.venue.<env>), applied by `useVenueScope()`
 * for the current env from useEnv(). See ./scope.ts for precedence and docs/VENUES.md for usage.
 */
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useEnv, type EnvName } from '../config/env';
import {
  ALL_VENUES,
  loadStoredOverride,
  parseOverride,
  readBuildVenueConfig,
  resolveVenueScope,
  saveStoredOverride,
  type VenueBuildConfig,
  type VenueSource,
} from './scope';

interface State {
  build: VenueBuildConfig;
  /** Bumped on every override change (overrides themselves live in `overrides`, loaded lazily). */
  version: number;
  /** Portfolio panels: also show rows from other venues (read-only). Session only. */
  showOtherVenues: boolean;
}

let state: State = fresh(readBuildVenueConfig());
/** Per-env override (undefined = none, ALL_VENUES = explicit all), lazily read from storage on first use. */
let overrides = new Map<EnvName, string | undefined>();
const listeners = new Set<() => void>();

function fresh(build: VenueBuildConfig): State {
  return { build, version: 0, showOtherVenues: false };
}

function emit() {
  for (const l of listeners) l();
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

function overrideFor(env: EnvName): string | undefined {
  // Lazy load without emitting: it is what storage already held, so no subscriber is stale.
  if (!overrides.has(env)) overrides.set(env, loadStoredOverride(env));
  return overrides.get(env);
}

export const venueStore = {
  getState: () => state,
  build: () => state.build,
  /** True when the env's scope can be changed at runtime (not locked by the build). */
  canOverride(env: EnvName): boolean {
    return !(state.build[env] !== null && !state.build.overrideAllowed);
  },
  resolve(env: EnvName) {
    return resolveVenueScope({ build: state.build[env], overrideAllowed: state.build.overrideAllowed, stored: overrideFor(env) });
  },
  /** venue id, null = all venues, undefined = clear the override (back to the build default). */
  setOverride(env: EnvName, venue: string | null | undefined): void {
    if (!venueStore.canOverride(env)) return;
    const v = venue === undefined ? undefined : venue === null ? ALL_VENUES : parseOverride(venue);
    saveStoredOverride(env, v);
    overrides.set(env, v);
    state = { ...state, version: state.version + 1 };
    emit();
  },
  /** Applies a raw `?venue=` value (id or "all"). Returns false when ignored (locked build or empty value). */
  applyUrl(env: EnvName, raw: string | null): boolean {
    const v = parseOverride(raw);
    if (v === undefined || !venueStore.canOverride(env)) return false;
    venueStore.setOverride(env, v === ALL_VENUES ? null : v);
    return true;
  },
  setShowOtherVenues(show: boolean): void {
    if (state.showOtherVenues === show) return;
    state = { ...state, showOtherVenues: show };
    emit();
  },
  /** Tests: reset everything, optionally with a build config. */
  reset(build: VenueBuildConfig = readBuildVenueConfig()): void {
    state = fresh(build);
    overrides = new Map();
    emit();
  },
};

export interface VenueScope {
  /** Scoped venue id, or null for all venues. */
  venue: string | null;
  /** venue id, or null = all venues. No-op when locked. */
  setVenue: (venue: string | null) => void;
  /** Drop the runtime override (back to the build default). */
  resetVenue: () => void;
  /** Build-time venue fixed and override UI hidden. */
  isLocked: boolean;
  source: VenueSource;
  /** Build-time venue for this env (the default when overrides are allowed). */
  buildVenue: string | null;
}

export function useVenueScope(): VenueScope {
  const { env } = useEnv();
  const name = env.name;
  const snap = useSyncExternalStore(subscribe, venueStore.getState, venueStore.getState);
  const resolved = useMemo(() => venueStore.resolve(name), [name, snap]); // eslint-disable-line react-hooks/exhaustive-deps
  const setVenue = useCallback((v: string | null) => venueStore.setOverride(name, v), [name]);
  const resetVenue = useCallback(() => venueStore.setOverride(name, undefined), [name]);
  return {
    venue: resolved.venue,
    isLocked: resolved.isLocked,
    source: resolved.source,
    buildVenue: snap.build[name],
    setVenue,
    resetVenue,
  };
}

/** Session toggle shared by the portfolio panels: show rows from other venues too. */
export function useShowOtherVenues(): [boolean, (v: boolean) => void] {
  const snap = useSyncExternalStore(subscribe, venueStore.getState, venueStore.getState);
  return [snap.showOtherVenues, venueStore.setShowOtherVenues];
}
